import React, { useState, useEffect, useMemo } from 'react';
import { UserProfile, ReportMetadata, SubstitutionConfig, VacationRange } from '../types';
import { supabase } from '../services/supabaseClient';
import { companyFilter } from '../services/companyScope';
import { useGesperrteKonten } from '../hooks/useGesperrteKonten';
import { ohneGesperrte } from '../services/gesperrteKonten';
import { Button } from './Button';
import { parseDateLocal } from '../services/dateService';
import { getSubstitutions, hasOverlap, mirrorLegacyConfig, weeksInRange, coverageSlots, uncoveredSlots, mergeAdjacentRanges, CoverageSlot, WeekSlot } from '../services/substitutionService';
import { getProjectRelation } from '../services/projectAccess';
import { useRechte } from '../hooks/useRechte';
import { SubstituteAssignment, downloadIcsContent, generateSubstituteIcs, substituteDueDates } from '../services/calendarService';
import { SubstitutionMailPlan, assignmentsFor, buildIcsFilename, groupBySubstitute, sendSubstitutionPlans } from '../services/substitutionMailer';
import { Calendar, Palmtree, User, Users, CheckSquare, Square, X, Loader2, Save, List, Trash2, Clock, CheckCircle2, AlertCircle, CalendarClock, CalendarPlus, Search, Filter, LayoutGrid, GripVertical, Mail, Download, Send } from 'lucide-react';
import type { ListProject } from '../services/listProject';

interface Props {
  projects: ListProject[];
  onSave: (projectIds: string[], subConfig: any) => Promise<void>;
  onClose: () => void;
  userProfile: UserProfile;
  initialStartDate?: string;
  initialEndDate?: string;
}

const formatDate = (dateString: string) => {
    const d = parseDateLocal(dateString);
    if (!d) return '';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDayMonth = (dateString: string) => {
    const d = parseDateLocal(dateString);
    if (!d) return '';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

const weekKey = (w: WeekSlot) => `${w.year}-${w.kw}`;
// Stabil über den Termin, nicht über den Beginn: `uncoveredSlots` kürzt einen
// Abschnitt, sobald eine Teilvertretung dazukommt — die Zuweisung bleibt dieselbe.
const slotKey = (s: CoverageSlot) => `${weekKey(s.week)}|${s.dueDate ?? '-'}`;

const WEEKDAY_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
/** Chip-Reihenfolge Mo … So, Wochen ohne Termin zuletzt. */
const weekdayOrder = (wd: number | null) => (wd === null ? 7 : (wd + 6) % 7);

/**
 * Zeilenschlüssel eines Vertreters in der Matrix: die E-Mail; alte Einträge
 * ohne E-Mail laufen über den Namen, ganz ohne Angabe über eine eigene Zeile —
 * sonst fielen sie aus der Matrix heraus, zählten aber bei „Geplante" mit.
 */
const KEY_OHNE_VERTRETER = 'ohne-vertreterangabe';
const substituteKey = (e: SubstitutionConfig): string =>
    (e.substituteEmail || '').trim().toLowerCase() || (e.substituteName ? `name:${e.substituteName.trim().toLowerCase()}` : KEY_OHNE_VERTRETER);

const fullName = (u: UserProfile) => `${u.first_name || ''} ${u.last_name || ''}`.trim();

/**
 * Kurzlabel einer gespeicherten Vertretung in einer Matrixwoche: leer, wenn
 * sie die ganze Woche abdeckt; sonst die Wochentage ihrer Termine („Di",
 * „Di/Do"), ohne Termin der abgedeckte Zeitraum.
 */
const savedSlotLabel = (dues: string[], entry: SubstitutionConfig, w: WeekSlot): string => {
    const from = entry.startDate > w.weekStart ? entry.startDate : w.weekStart;
    const to = entry.endDate < w.weekEnd ? entry.endDate : w.weekEnd;
    if (from === w.weekStart && to === w.weekEnd) return '';
    const days = dues.filter(d => d >= from && d <= to);
    if (days.length > 0) return days.map(d => WEEKDAY_SHORT[parseDateLocal(d)?.getDay() ?? 0]).join('/');
    return from === to ? formatDayMonth(from) : `${formatDayMonth(from)}–${formatDayMonth(to)}`;
};

/** Geplante Matrix-Zuweisung (noch nicht gespeichert). */
interface PendingAssignment {
    projectId: string;
    slot: CoverageSlot;
    user: UserProfile;
}

/** Gegriffener Chip: ein Projekt und — bei mehreren Terminen je Woche — der Wochentag. */
interface GrabbedChip {
    projectId: string;
    weekday: number | null;
}

/** Eine Zeile der Projektauswahl ("Neu planen"), gruppiert nach faellig / weitere / vertreten. */
interface PlanRow {
    p: ListProject;
    meta: ReportMetadata;
    dueDateStr: string | null;
    isCovered: boolean;
    coveringNames: string;
}

export const HolidayPlanner: React.FC<Props> = ({ projects, onSave, onClose, userProfile, initialStartDate, initialEndDate }) => {
  const [activeTab, setActiveTab] = useState<'plan' | 'matrix' | 'manage'>('plan');
  // Alle Profile der Firma (inkl. eigenem) — Vertreterliste und Admin-Auswahl.
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  // Admins und wer das Recht „Urlaub für andere planen“ hat, planen auch für
  // Mitarbeiter: wessen Projekte gerade vertreten werden. Nur Oberfläche
  // (Plan §11) — Admins haben jedes Recht, ohne Migration gilt die
  // Systemrolle, also wie bisher nur Admins.
  const rechte = useRechte(userProfile.role);
  const darfFuerAnderePlanen = rechte.hat('urlaub.fuer_andere');
  const [planForId, setPlanForId] = useState<string>(userProfile.id);
  // Hier eingetragene Urlaube, bis die Profile neu geladen sind.
  const [vacationOverrides, setVacationOverrides] = useState<Record<string, VacationRange[]>>({});
  const [vacationBusy, setVacationBusy] = useState(false);
  const [vacationStatus, setVacationStatus] = useState<string | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Vertreter nach dem Speichern automatisch per Mail informieren (abschaltbar).
  const [notifySubstitutes, setNotifySubstitutes] = useState(true);
  const [mailStatus, setMailStatus] = useState<string | null>(null);
  const [mailBusy, setMailBusy] = useState(false);
  const [data, setData] = useState({
      substituteEmail: '',
      startDate: initialStartDate || '',
      endDate: initialEndDate || ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [manageFilter, setManageFilter] = useState('');
  // Matrix: geplante, noch ungespeicherte Zuweisungen + aktuell gegriffenes Projekt
  const [pending, setPending] = useState<PendingAssignment[]>([]);
  const [grabbed, setGrabbed] = useState<GrabbedChip | null>(null);
  // Matrix: Projektsuche (Name, Nummer, Adresse) — filtert Ablage und Zellen.
  const [matrixSearch, setMatrixSearch] = useState('');
  const [matrixViewMode, setMatrixViewMode] = useState<'projects-above' | 'projects-right'>('projects-above');
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // HTML5-DnD scrollt Container nicht automatisch — beim Ziehen nahe
  // Ober-/Unterkante des Modal-Bodys nachhelfen, sonst sind untere
  // Vertreter-Zeilen per Drag unerreichbar.
  const dragAutoScroll = (e: React.DragEvent) => {
      const el = scrollRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const zone = 56;
      if (e.clientY < rect.top + zone) el.scrollTop -= 16;
      else if (e.clientY > rect.bottom - zone) el.scrollTop += 16;
  };

  // Wer in Urlaub geht. Ohne Admin-Auswahl immer der angemeldete Nutzer; das
  // frisch geladene Profil hat den aktuellen Urlaubsplan, das aus den Props
  // stammt vom Login.
  const planFor: UserProfile = useMemo(
      () => profiles.find(u => u.id === planForId) || userProfile,
      [profiles, planForId, userProfile]
  );
  const planningForOther = planFor.id !== userProfile.id;
  const planForName = fullName(planFor) || planFor.email;
  const planForEmail = (planFor.email || '').trim().toLowerCase();

  // Vertreter: alle Kollegen außer dem, der in Urlaub geht — plant ein Admin
  // für einen Mitarbeiter, steht der Admin selbst also mit zur Wahl.
  const users = useMemo(
      () => profiles.filter(u => u.id !== planFor.id && u.first_name && u.last_name),
      [profiles, planFor.id]
  );

  // Gesperrte Mitglieder (Nr. 148) stehen weder als Vertreter noch als Zeile
  // der Matrix zur Wahl. `users` bleibt vollständig, weil es auch Namen zu
  // gespeicherten Vertretungen auflöst; deren Einträge erscheinen als
  // schreibgeschützte Zeile (foreignSubstitutes).
  const gesperrteKonten = useGesperrteKonten();
  const vertreterAuswahl = useMemo(() => ohneGesperrte(users, gesperrteKonten), [users, gesperrteKonten]);
  // Gesperrt, aber mit noch ungespeicherten Zuweisungen (die Sperrliste kam
  // erst danach): Zeile unter der echten id behalten, damit die Einträge
  // sichtbar und entfernbar bleiben — schreibgeschützt, ohne neue Zuweisung.
  const gesperrteMitOffenen = useMemo(
      () => users.filter(u => gesperrteKonten.has(u.id) && pending.some(a => a.user.id === u.id)),
      [users, gesperrteKonten, pending]
  );
  const gesperrteMails = useMemo(
      () => new Set(users.filter(u => gesperrteKonten.has(u.id)).map(u => (u.email || '').trim().toLowerCase())),
      [users, gesperrteKonten]
  );

  // Für wen ein Admin planen kann: Mitarbeiter mit Namen, ohne externe Freelancer.
  const plannableEmployees = useMemo(
      () => ohneGesperrte(profiles, gesperrteKonten, planForId)
          .filter(u => u.first_name && u.last_name && u.role !== 'freelancer')
          .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || '')),
      [profiles, gesperrteKonten, planForId]
  );

  /** Kommende Urlaube der geplanten Person — Schnellwahl für den Zeitraum. */
  const upcomingVacations = useMemo(() => {
      const list = vacationOverrides[planFor.id] ?? planFor.vacation_plan ?? [];
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return list
          .filter(v => { const end = parseDateLocal(v.endDate); return !!end && end >= today; })
          .sort((a, b) => a.startDate.localeCompare(b.startDate));
  }, [vacationOverrides, planFor]);

  // Planbar: eigene Projekte, Koordinator-Projekte und Projekte mit aktiver Deputy-Rolle.
  // Bewusst KEINE Kette: Projekte, die nur per Urlaubsvertretung übertragen sind, fehlen hier.
  // Nur Ausführungs-Verantwortung: Wer nur Verantwortliche Planungsphase ist,
  // hinterlässt im Urlaub keine Begehung, die jemand übernehmen müsste —
  // auch dann nicht, wenn sie gerade selbst vertritt (sonst Vertretungskette).
  const myProjects = useMemo(() => {
      const isPlannable = (p: ListProject): boolean =>
          getProjectRelation(p, { id: planFor.id, email: planForEmail }).isResponsibleAusfuehrungDauerhaft;
      const groups: Record<string, ListProject> = {};
      projects.forEach(p => {
          const meta = p.data?.metadata as ReportMetadata;
          if (!meta) return;
          if (!isPlannable(p)) return;
          if (meta.globalData?.status === 'finished' || meta.globalData?.status === 'completed') return;
          const key = meta.projectNumber ? `grp_${meta.projectNumber}` : `id_${p.id}`;
          if (!groups[key]) {
              groups[key] = p;
          } else {
              const currentNr = meta.inspectionNumber || 0;
              const existingNr = groups[key].data.metadata?.inspectionNumber || 0;
              if (currentNr > existingNr) groups[key] = p;
              else if (currentNr === existingNr && new Date(p.updated_at) > new Date(groups[key].updated_at)) groups[key] = p;
          }
      });
      return Object.values(groups).sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }, [projects, planFor.id, planForEmail]);

  // Manage-Tab: alle nicht abgelaufenen aktiven Einträge, projektübergreifend geflacht.
  const managedEntries = useMemo(() => {
      const now = new Date();
      return myProjects.flatMap(p => {
          const meta = p.data?.metadata as ReportMetadata;
          return getSubstitutions(meta)
              .map((entry, idx) => ({ p, entry, idx }))
              .filter(({ entry }) => {
                  if (!entry.active) return false;
                  const end = parseDateLocal(entry.endDate);
                  if (!end) return false;
                  end.setHours(23, 59, 59, 999);
                  return now <= end;
              });
      });
  }, [myProjects]);

  const uniqueSubstitutes = useMemo(() => {
      const subs = new Set<string>();
      managedEntries.forEach(({ entry }) => { if (entry.substituteName) subs.add(entry.substituteName); });
      return Array.from(subs).sort();
  }, [managedEntries]);

  const filteredManagedEntries = useMemo(() => {
      if (!manageFilter) return managedEntries;
      return managedEntries.filter(({ entry }) => entry.substituteName === manageFilter);
  }, [managedEntries, manageFilter]);

  /** Aktive Vertretungen je Vertreter gebündelt — Basis für Nachversand und ICS-Download. */
  const managedPlans = useMemo<SubstitutionMailPlan[]>(
      () => groupBySubstitute(filteredManagedEntries.map(({ p, entry }) => ({ project: p, entry }))),
      [filteredManagedEntries]
  );

  const filteredProjects = useMemo(() => {
      if (!searchTerm) return myProjects;
      const lower = searchTerm.toLowerCase();
      return myProjects.filter(p => {
          const meta = p.data.metadata as ReportMetadata;
          return (p.name || '').toLowerCase().includes(lower) || (meta.projectNumber || '').toLowerCase().includes(lower);
      });
  }, [myProjects, searchTerm]);

  useEffect(() => {
      const fetchUsers = async () => {
          let query = supabase.from('profiles').select('*');
          const filter = companyFilter(userProfile);
          if (filter) query = query.eq(filter.column, filter.value);
          const { data, error } = await query;
          if (error) { console.error('Error fetching users:', error); return; }
          if (data) setProfiles(data as UserProfile[]);
      };
      fetchUsers();
  }, [userProfile.id, userProfile.company_id, userProfile.company_name]);

  useEffect(() => {
      if (data.startDate && data.endDate && activeTab === 'plan') {
          // Clear selection when dates change — user picks projects manually
          setSelectedProjectIds(new Set());
      }
      // Zeitraum geändert → geplante Matrix-Zuweisungen passen nicht mehr zu den Wochen
      setPending([]);
      setGrabbed(null);
  }, [data.startDate, data.endDate]);

  // Anderer Mitarbeiter gewählt → andere Projekte, andere Vertreterliste.
  // Auswahl, ungespeicherte Zuweisungen und Filter gehören zum vorigen.
  // Mit ungespeicherten Zuweisungen ist die Auswahl gesperrt (siehe Leiste oben).
  const changePlanFor = (id: string) => {
      if (id === planForId || pending.length > 0) return;
      setPlanForId(id);
      setSelectedProjectIds(new Set());
      setPending([]);
      setGrabbed(null);
      setManageFilter('');
      setSearchTerm('');
      setMatrixSearch('');
      setData(prev => ({ ...prev, substituteEmail: '' }));
      setSaveError(null);
      setMailStatus(null);
      setVacationStatus(null);
  };

  /**
   * Trägt den gewählten Zeitraum als Urlaub der geplanten Person ein (dieselbe
   * Liste wie „Profil → Urlaubsplanung"). Liest frisch und hängt nur an —
   * der Mitarbeiter kann parallel selbst eingetragen haben.
   */
  const handleAddVacation = async () => {
      const { startDate, endDate } = data;
      if (!startDate || !endDate || startDate > endDate) return;
      setVacationBusy(true);
      setVacationStatus(null);
      setSaveError(null);
      try {
          const { data: row, error } = await supabase.from('profiles').select('vacation_plan').eq('id', planFor.id).single();
          // 42703 = Spalte fehlt: Migration Nr. 145 ist in dieser Datenbank noch nicht gelaufen.
          if (error) throw new Error(error.code === '42703' ? 'Urlaubsliste in der Datenbank fehlt noch (Migration Nr. 145).' : error.message);
          const current: VacationRange[] = Array.isArray(row?.vacation_plan) ? row.vacation_plan : [];
          if (!current.some(v => v.startDate === startDate && v.endDate === endDate)) {
              const next = [...current, { id: crypto.randomUUID(), startDate, endDate }]
                  .sort((a, b) => a.startDate.localeCompare(b.startDate));
              const { data: written, error: writeError } = await supabase
                  .from('profiles')
                  .update({ vacation_plan: next })
                  .eq('id', planFor.id)
                  .select('id');
              if (writeError) throw new Error(writeError.message);
              // RLS schluckt fremde Zeilen still: 0 Zeilen = nicht berechtigt.
              if (!written || written.length === 0) throw new Error('Keine Berechtigung für dieses Profil.');
              setVacationOverrides(prev => ({ ...prev, [planFor.id]: next }));
          } else {
              setVacationOverrides(prev => ({ ...prev, [planFor.id]: current }));
          }
          setVacationStatus(`Urlaub ${formatDate(startDate)}–${formatDate(endDate)} bei ${planForName} eingetragen.`);
      } catch (e) {
          setSaveError(`Urlaub konnte nicht eingetragen werden: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`);
      } finally {
          setVacationBusy(false);
      }
  };

  const toggleProject = (id: string) => {
      const newSet = new Set(selectedProjectIds);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
      setSelectedProjectIds(newSet);
  };

  const toggleAll = () => {
      const allFilteredSelected = filteredProjects.every(p => selectedProjectIds.has(p.id));
      const newSet = new Set(selectedProjectIds);
      if (allFilteredSelected) filteredProjects.forEach(p => newSet.delete(p.id));
      else filteredProjects.forEach(p => newSet.add(p.id));
      setSelectedProjectIds(newSet);
  };

  /**
   * Erster Begehungstermin im Zeitraum. Nutzt dieselbe Aufzählung wie die
   * ICS-Erzeugung (`substituteDueDates`), damit Planungsliste und
   * Vertretungsmail nie unterschiedliche Termine behaupten.
   */
  const getDueDateInPeriod = (project: ListProject, start: string, end: string): string | null => {
      if (!start || !end) return null;
      try {
          return substituteDueDates(project, { active: true, startDate: start, endDate: end })[0] || null;
      } catch (e) { console.error('getDueDateInPeriod error:', e); }
      return null;
  };

  /**
   * Liest das Projekt frisch, wendet `updater` auf die Vertretungsliste an und
   * schreibt Array + Legacy-Spiegel zurück. `updater` darf werfen (z.B. Overlap).
   */
  const patchSubstitutions = async (projectId: string, updater: (entries: SubstitutionConfig[]) => SubstitutionConfig[]) => {
      // Optimistic Lock über updated_at: schlägt ein anderer Client dazwischen,
      // trifft das Update 0 Zeilen und wir lesen/patchen erneut (max. 3 Versuche).
      for (let attempt = 0; attempt < 3; attempt++) {
          const { data: project, error: readError } = await supabase
              .from('projects')
              .select('data, updated_at')
              .eq('id', projectId)
              .single();

          if (readError || !project) throw new Error(`Projekt ${projectId} nicht gefunden: ${readError?.message}`);

          const currentData = project.data;
          const currentEntries = getSubstitutions(currentData?.metadata);
          const newEntries = updater(currentEntries);
          const updatedData = {
              ...currentData,
              metadata: {
                  ...currentData.metadata,
                  substitutions: newEntries,
                  substitutionConfig: mirrorLegacyConfig(newEntries)
              }
          };

          const { data: updatedRows, error: writeError } = await supabase
              .from('projects')
              .update({ data: updatedData, updated_at: new Date().toISOString() })
              .eq('id', projectId)
              .eq('updated_at', project.updated_at)
              .select('id, updated_at');

          if (writeError) throw new Error(`Speichern fehlgeschlagen für ${projectId}: ${writeError.message}`);
          if (updatedRows && updatedRows.length > 0) return;
      }
      throw new Error('Projekt wurde zwischenzeitlich geändert. Bitte erneut versuchen.');
  };

  const buildEntry = (user: UserProfile, startDate: string, endDate: string): SubstitutionConfig => ({
      active: true,
      substituteId: user.id,
      substituteName: `${user.first_name} ${user.last_name}`,
      substituteEmail: user.email,
      startDate,
      endDate
  });

  // In der Vertretungsmail: „Geplant von …". Plant ein Admin für einen
  // Mitarbeiter, steht beides drin — der Vertreter soll wissen, für wen.
  const myName = fullName(userProfile);
  const plannerName = planningForOther
      ? `${myName || userProfile.email} für ${planForName}`
      : myName || undefined;

  /**
   * Verschickt je Vertreter EINE Mail mit Projektliste, Zeitraum, Terminen und
   * ICS-Anhang. Läuft NACH dem Speichern und wirft nie: die Vertretung steht
   * dann schon in der Datenbank, ein Mailfehler darf sie nicht zurücknehmen.
   */
  const notifySubstitutePlans = async (assignments: SubstituteAssignment[], force = false) => {
      if ((!notifySubstitutes && !force) || assignments.length === 0) return;
      setMailBusy(true);
      setMailStatus('Vertreter werden per E-Mail informiert …');
      try {
          const results = await sendSubstitutionPlans(groupBySubstitute(assignments), plannerName);
          const ok = results.filter(r => r.ok);
          const failed = results.filter(r => !r.ok);
          const parts: string[] = [];
          if (ok.length > 0) parts.push(`E-Mail an ${ok.map(r => r.substituteName).join(', ')} versandt.`);
          if (failed.length > 0) parts.push(`Versand fehlgeschlagen: ${failed.map(r => `${r.substituteName} (${r.error})`).join('; ')}`);
          setMailStatus(parts.join(' ') || null);
      } catch (e: any) {
          setMailStatus(`Versand fehlgeschlagen: ${e?.message || 'Unbekannter Fehler'}`);
      } finally {
          setMailBusy(false);
      }
  };

  const handleSave = async () => {
      setSaveError(null);

      if (selectedProjectIds.size === 0) { setSaveError("Bitte wählen Sie mindestens ein Projekt aus."); return; }
      if (!data.substituteEmail) { setSaveError("Bitte wählen Sie einen Vertreter."); return; }
      if (!data.startDate || !data.endDate) { setSaveError("Bitte wählen Sie einen Zeitraum."); return; }
      const selStart = parseDateLocal(data.startDate);
      const selEnd = parseDateLocal(data.endDate);
      if (!selStart || !selEnd || selStart > selEnd) { setSaveError("Startdatum muss vor Enddatum liegen."); return; }

      const selectedUser = users.find(u => u.email === data.substituteEmail);
      if (!selectedUser) { setSaveError(`Vertreter mit E-Mail "${data.substituteEmail}" nicht gefunden.`); return; }

      const newEntry = buildEntry(selectedUser, data.startDate, data.endDate);

      setLoading(true);
      setMailStatus(null);
      const projectIds: string[] = Array.from(selectedProjectIds);
      const errors: string[] = [];
      const savedIds: string[] = [];

      for (const pid of projectIds) {
          try {
              await patchSubstitutions(pid, entries => {
                  if (hasOverlap(entries, data.startDate, data.endDate)) {
                      throw new Error('Zeitraum überschneidet eine bestehende Vertretung.');
                  }
                  return [...entries, newEntry];
              });
              savedIds.push(pid);
          } catch (e: any) {
              console.error(`[HolidayPlanner] Error saving project ${pid}:`, e);
              const projName = myProjects.find(p => p.id === pid)?.name || pid;
              errors.push(`${projName}: ${e.message}`);
          }
      }

      setLoading(false);

      // Nur für tatsächlich gespeicherte Projekte informieren — sonst kündigt
      // die Mail Begehungen an, die gar nicht zugewiesen wurden.
      const savedProjects = myProjects.filter(p => savedIds.includes(p.id));
      await notifySubstitutePlans(assignmentsFor(savedProjects, newEntry));

      if (errors.length > 0) {
          setSaveError(`${projectIds.length - errors.length} von ${projectIds.length} gespeichert. Fehler:\n${errors.join('\n')}`);
      } else {
          let refreshFailed = false;
          try { await onSave([], newEntry); } catch { refreshFailed = true; }
          setActiveTab('manage');
          setSelectedProjectIds(new Set());
          setData(prev => ({ ...prev, substituteEmail: '' }));
          setSaveError(refreshFailed ? 'Gespeichert, aber die Ansicht konnte nicht aktualisiert werden. Bitte neu laden.' : null);
      }
  };

  const handleDownloadPlanIcs = (plan: SubstitutionMailPlan) => {
      downloadIcsContent(generateSubstituteIcs(plan.assignments), buildIcsFilename(plan));
  };

  const handleCancelSubstitution = async (project: ListProject, entry: SubstitutionConfig) => {
      if (!window.confirm(`Vertretung (${entry.substituteName}, ${formatDate(entry.startDate)}–${formatDate(entry.endDate)}) für "${project.name}" wirklich beenden?`)) return;
      setLoading(true);
      setSaveError(null);
      try {
          await patchSubstitutions(project.id, entries =>
              entries.map(e =>
                  e.startDate === entry.startDate && e.endDate === entry.endDate &&
                  (e.substituteEmail || '') === (entry.substituteEmail || '')
                      ? { ...e, active: false }
                      : e
              )
          );
          try { await onSave([], { active: false }); } catch {
              setSaveError('Storniert, aber die Ansicht konnte nicht aktualisiert werden. Bitte neu laden.');
          }
      } catch (e: any) {
          console.error('[HolidayPlanner] Cancel error:', e);
          setSaveError(`Fehler beim Stornieren: ${e?.message || 'Unbekannt'}`);
      } finally {
          setLoading(false);
      }
  };

  // ── Matrix ──────────────────────────────────────────────────────────────

  const weeks = useMemo(() => weeksInRange(data.startDate, data.endDate), [data.startDate, data.endDate]);

  /**
   * Begehungstermine je Projekt im Zeitraum — dieselbe Aufzählung wie
   * Vertretungsmail und ICS. Daraus entstehen die Abschnitte: mehrere Termine
   * pro Woche ergeben mehrere Chips, jeder an einen anderen Kollegen vergebbar.
   */
  const dueDatesByProject = useMemo(() => {
      const map = new Map<string, string[]>();
      if (weeks.length === 0) return map;
      myProjects.forEach(p => {
          try {
              map.set(p.id, substituteDueDates(p, { active: true, startDate: data.startDate, endDate: data.endDate }, 400));
          } catch (e) {
              console.error('[HolidayPlanner] Termine nicht berechenbar:', e);
              map.set(p.id, []);
          }
      });
      return map;
  }, [myProjects, weeks, data.startDate, data.endDate]);

  const slotsByProject = useMemo(() => {
      const map = new Map<string, CoverageSlot[]>();
      myProjects.forEach(p => map.set(p.id, coverageSlots(weeks, dueDatesByProject.get(p.id) || [])));
      return map;
  }, [myProjects, weeks, dueDatesByProject]);

  /** Offene Abschnitte pro Projekt: weder gespeicherte Vertretung noch pending Zuweisung. */
  const openSlotsByProject = useMemo(() => {
      const map = new Map<string, CoverageSlot[]>();
      myProjects.forEach(p => {
          const meta = p.data?.metadata as ReportMetadata;
          const open = uncoveredSlots(meta, slotsByProject.get(p.id) || []).filter(s =>
              !pending.some(a => a.projectId === p.id && slotKey(a.slot) === slotKey(s))
          );
          map.set(p.id, open);
      });
      return map;
  }, [myProjects, slotsByProject, pending]);

  /** Trifft die Matrix-Suche dieses Projekt? Leere Suche trifft alles. */
  const matchesMatrixSearch = useMemo(() => {
      const term = matrixSearch.trim().toLowerCase();
      type Searchable = { name?: string; data?: { metadata?: ReportMetadata } } | undefined;
      if (!term) return (_p: Searchable) => true;
      return (p: Searchable) => {
          const meta = p?.data?.metadata;
          return [p?.name, meta?.projectNumber, meta?.projectName, meta?.projectAddress]
              .some(v => String(v || '').toLowerCase().includes(term));
      };
  }, [matrixSearch]);

  const shelfProjects = useMemo(
      () => myProjects.filter(p => (openSlotsByProject.get(p.id) || []).length > 0 && matchesMatrixSearch(p)),
      [myProjects, openSlotsByProject, matchesMatrixSearch]
  );
  /** Offene Projekte ohne Suchfilter — trennt „alles vertreten" von „Suche trifft nichts". */
  const openProjectCount = useMemo(
      () => myProjects.filter(p => (openSlotsByProject.get(p.id) || []).length > 0).length,
      [myProjects, openSlotsByProject]
  );

  /** Größte Zahl Termine in einer Woche — „so viele Chips, wie im Projekt hinterlegt". */
  const termsPerWeek = (projectId: string): number => {
      const perWeek = new Map<string, number>();
      (slotsByProject.get(projectId) || []).forEach(s => {
          if (s.weekday !== null) perWeek.set(weekKey(s.week), (perWeek.get(weekKey(s.week)) || 0) + 1);
      });
      return Math.max(0, ...Array.from(perWeek.values()));
  };

  /** Gespeicherte aktive Einträge, indexiert nach `email|weekKey` (statt O(Projekte) pro Zelle). */
  const savedByCell = useMemo(() => {
      const map = new Map<string, { p: ListProject; entry: SubstitutionConfig; label: string }[]>();
      if (weeks.length === 0) return map;
      myProjects.forEach(p => {
          const meta = p.data?.metadata as ReportMetadata;
          const dues = dueDatesByProject.get(p.id) || [];
          getSubstitutions(meta).filter(e => e.active).forEach(entry => {
              const mail = substituteKey(entry);
              if (!mail) return;
              weeks.forEach(w => {
                  if (hasOverlap([entry], w.weekStart, w.weekEnd)) {
                      const k = `${mail}|${weekKey(w)}`;
                      const list = map.get(k) || [];
                      list.push({ p, entry, label: savedSlotLabel(dues, entry, w) });
                      map.set(k, list);
                  }
              });
          });
      });
      return map;
  }, [myProjects, weeks, dueDatesByProject]);

  const savedCellEntries = (u: UserProfile, w: WeekSlot) =>
      savedByCell.get(`${(u.email || '').trim().toLowerCase()}|${weekKey(w)}`) || [];

  /**
   * Vertreter mit gespeicherter Vertretung im Zeitraum, die keine Zeile in der
   * Kollegenliste haben (Profil ohne Namen, andere Firma, E-Mail geändert).
   * Ohne eigene Zeile wären ihre Einträge in der Matrix unsichtbar.
   */
  const foreignSubstitutes = useMemo<UserProfile[]>(() => {
      const known = new Set([...vertreterAuswahl, ...gesperrteMitOffenen].map(u => (u.email || '').trim().toLowerCase()));
      const found = new Map<string, string>();
      savedByCell.forEach(list => list.forEach(({ entry }) => {
          const mail = substituteKey(entry);
          if (mail && !known.has(mail) && !found.has(mail)) found.set(mail, entry.substituteName || (mail === KEY_OHNE_VERTRETER ? 'Ohne Vertreterangabe' : mail));
      }));
      return Array.from(found.entries())
          .sort((a, b) => a[1].localeCompare(b[1]))
          .map(([email, name]) => ({ id: `fremd:${email}`, email, first_name: name, last_name: '', role: 'user' }));
  }, [vertreterAuswahl, gesperrteMitOffenen, savedByCell]);

  /**
   * Wie viele der geplanten Vertretungen (Reiter „Geplante") die Matrix zeigt
   * — und warum die übrigen fehlen. Grundlage sind dieselben Einträge wie
   * dort: aktiv und noch nicht abgelaufen.
   */
  const matrixCoverage = useMemo(() => {
      if (weeks.length === 0) return null;
      const rangeStart = weeks[0].weekStart;
      const rangeEnd = weeks[weeks.length - 1].weekEnd;
      let outside = 0;
      let bySearch = 0;
      managedEntries.forEach(({ p, entry }) => {
          if (!hasOverlap([entry], rangeStart, rangeEnd)) outside++;
          else if (!matchesMatrixSearch(p)) bySearch++;
      });
      const total = managedEntries.length;
      return { total, shown: total - outside - bySearch, outside, bySearch };
  }, [weeks, managedEntries, matchesMatrixSearch]);

  /**
   * Zeitraum so erweitern, dass alle geplanten Vertretungen in der Matrix
   * stehen; der bisher gewählte Zeitraum bleibt enthalten. Ein Zeitraumwechsel
   * verwirft ungespeicherte Zuweisungen — der Knopf ist dann gesperrt.
   */
  const showAllPlanned = () => {
      if (pending.length > 0 || managedEntries.length === 0) return;
      const starts = managedEntries.map(({ entry }) => entry.startDate);
      const ends = managedEntries.map(({ entry }) => entry.endDate);
      if (data.startDate) starts.push(data.startDate);
      if (data.endDate) ends.push(data.endDate);
      const startDate = starts.reduce((a, b) => (b < a ? b : a));
      const endDate = ends.reduce((a, b) => (b > a ? b : a));
      setData(prev => ({ ...prev, startDate, endDate }));
      setSaveError(null);
  };

  const assignToCell = (chip: GrabbedChip, u: UserProfile, w: WeekSlot) => {
      setSaveError(null);
      const proj = myProjects.find(p => p.id === chip.projectId);
      if (!proj) return;
      const day = chip.weekday !== null ? ` am ${WEEKDAY_SHORT[chip.weekday]}` : '';
      const matches = (s: CoverageSlot) => weekKey(s.week) === weekKey(w) && s.weekday === chip.weekday;
      const slot = (openSlotsByProject.get(chip.projectId) || []).find(matches);
      if (!slot) {
          const exists = (slotsByProject.get(chip.projectId) || []).some(matches);
          setSaveError(exists
              ? `"${proj.name || 'Unbenannt'}" ist in KW ${w.kw}${day} bereits vertreten.`
              : `"${proj.name || 'Unbenannt'}" hat in KW ${w.kw} keinen Termin${day}.`);
          return;
      }
      setPending(prev => [...prev, { projectId: chip.projectId, slot, user: u }]);
      setGrabbed(null);
  };

  const removePending = (a: PendingAssignment) => {
      setPending(prev => prev.filter(x => !(x.projectId === a.projectId && slotKey(x.slot) === slotKey(a.slot) && x.user.id === a.user.id)));
  };

  /**
   * Zuweisungen eines Projekts → Vertretungseinträge. Übernimmt ein Kollege
   * aneinanderstoßende Abschnitte derselben Woche, wird daraus EIN Eintrag;
   * über Wochengrenzen bleibt es wie bisher ein Eintrag je Woche.
   */
  const entriesFromPending = (assignments: PendingAssignment[]): SubstitutionConfig[] => {
      const groups = new Map<string, { user: UserProfile; ranges: { startDate: string; endDate: string }[] }>();
      assignments.forEach(a => {
          const k = `${a.user.id}|${weekKey(a.slot.week)}`;
          const g = groups.get(k) || { user: a.user, ranges: [] };
          g.ranges.push({ startDate: a.slot.startDate, endDate: a.slot.endDate });
          groups.set(k, g);
      });
      return Array.from(groups.values())
          .flatMap(g => mergeAdjacentRanges(g.ranges).map(r => buildEntry(g.user, r.startDate, r.endDate)))
          .sort((a, b) => a.startDate.localeCompare(b.startDate));
  };

  const handleSaveMatrix = async () => {
      if (pending.length === 0) return;
      setLoading(true);
      setSaveError(null);
      // Stapel festhalten: Zuweisungen, die während des Speicherns dazukommen,
      // gehören nicht dazu und dürfen danach nicht mit weggeräumt werden.
      const batch = pending;
      const byProject = new Map<string, PendingAssignment[]>();
      batch.forEach(a => {
          const list = byProject.get(a.projectId) || [];
          list.push(a);
          byProject.set(a.projectId, list);
      });

      const errors: string[] = [];
      const saved: string[] = [];
      // Je Vertreter entsteht daraus EINE Mail über alle seine Abschnitte.
      const mailAssignments: SubstituteAssignment[] = [];
      setMailStatus(null);
      for (const [pid, assignments] of Array.from(byProject.entries())) {
          let newEntries: SubstitutionConfig[] = [];
          try {
              await patchSubstitutions(pid, entries => {
                  // Abschnitte gegen den frisch gelesenen Stand kürzen: eine
                  // inzwischen dazugekommene Teilvertretung (z. B. nur Montag)
                  // ließe den gemerkten Zeitraum sonst überlappen.
                  const current = assignments.map(a => {
                      const [slot] = uncoveredSlots({ substitutions: entries } as ReportMetadata, [a.slot]);
                      if (!slot) throw new Error(`KW ${a.slot.week.kw}${a.slot.dueDate ? ` (${formatDate(a.slot.dueDate)})` : ''} ist inzwischen vertreten.`);
                      return { ...a, slot };
                  });
                  newEntries = entriesFromPending(current);
                  let next = entries;
                  for (const e of newEntries) {
                      if (hasOverlap(next, e.startDate, e.endDate)) {
                          throw new Error(`${formatDate(e.startDate)}–${formatDate(e.endDate)} überschneidet eine bestehende Vertretung.`);
                      }
                      next = [...next, e];
                  }
                  return next;
              });
              saved.push(pid);
              const project = myProjects.find(p => p.id === pid);
              if (project) newEntries.forEach(entry => mailAssignments.push({ project, entry }));
          } catch (e: any) {
              const projName = myProjects.find(p => p.id === pid)?.name || pid;
              errors.push(`${projName}: ${e.message}`);
          }
      }
      // Erst Parent-Refresh (holt gespeicherte Einträge in die Props), dann pending leeren —
      // sonst erscheinen gespeicherte Abschnitte kurz wieder als offen.
      let refreshFailed = false;
      try { await onSave([], {}); } catch { refreshFailed = true; }
      setLoading(false);

      await notifySubstitutePlans(mailAssignments);

      setPending(prev => prev.filter(a => !(batch.includes(a) && saved.includes(a.projectId))));
      if (errors.length > 0) {
          setSaveError(`Fehler:\n${errors.join('\n')}`);
      } else if (refreshFailed) {
          setSaveError('Gespeichert, aber die Ansicht konnte nicht aktualisiert werden. Bitte neu laden.');
      } else {
          setActiveTab('manage');
      }
  };

  const isGrabbed = (projectId: string, weekday: number | null) =>
      !!grabbed && grabbed.projectId === projectId && grabbed.weekday === weekday;

  /** Ziehen oder Antippen — beide Wege für Projekt-Chip und Wochentags-Chip. */
  const chipHandlers = (projectId: string, weekday: number | null) => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
          e.dataTransfer.setData('text/plain', `${projectId}|${weekday ?? ''}`);
          e.dataTransfer.effectAllowed = 'copy';
          setGrabbed({ projectId, weekday });
      },
      onDragEnd: () => setGrabbed(null),
      onClick: () => setGrabbed(isGrabbed(projectId, weekday) ? null : { projectId, weekday }),
  });

  const parseChip = (raw: string): GrabbedChip | null => {
      const i = raw.lastIndexOf('|');
      if (i <= 0) return null;
      const wd = raw.slice(i + 1);
      const weekday = wd === '' ? null : Number(wd);
      if (weekday !== null && !Number.isInteger(weekday)) return null;
      return { projectId: raw.slice(0, i), weekday };
  };

  const projectChip = (p: ListProject) => {
      const meta = p.data?.metadata as ReportMetadata;
      const open = openSlotsByProject.get(p.id) || [];
      const head = (
          <>
              {meta?.projectNumber && <span className="font-mono text-[10px] text-slate-400 shrink-0">{meta.projectNumber}</span>}
              <span className="truncate max-w-[260px]">{p.name || 'Unbenannt'}</span>
          </>
      );
      const perWeek = termsPerWeek(p.id);

      // Höchstens ein Termin je Woche: ein Chip für das ganze Projekt wie bisher.
      if (perWeek === 0) {
          const grabbedHere = isGrabbed(p.id, null);
          return (
              <div
                  key={p.id}
                  {...chipHandlers(p.id, null)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold cursor-grab select-none transition-all ${
                      grabbedHere ? 'border-blue-500 bg-blue-100 text-blue-800 ring-2 ring-blue-300' : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                  title={p.name || ''}
              >
                  <GripVertical className="w-3 h-3 text-slate-300 shrink-0" />
                  {head}
                  <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full shrink-0">{open.length} KW offen</span>
              </div>
          );
      }

      // Mehrere Termine je Woche: ein Chip je Wochentag, jeder einzeln vergebbar.
      const groups = new Map<number | null, number>();
      open.forEach(s => groups.set(s.weekday, (groups.get(s.weekday) || 0) + 1));
      const ordered = Array.from(groups.entries()).sort((a, b) => weekdayOrder(a[0]) - weekdayOrder(b[0]));
      return (
          <div key={p.id} className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-bold text-slate-700 select-none" title={p.name || ''}>
              {head}
              <span className="text-[10px] font-normal text-slate-400 shrink-0">{perWeek}× pro Woche</span>
              {ordered.map(([wd, count]) => {
                  const grabbedHere = isGrabbed(p.id, wd);
                  return (
                      <span
                          key={wd ?? 'w'}
                          {...chipHandlers(p.id, wd)}
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md border cursor-grab transition-all ${
                              grabbedHere ? 'border-blue-500 bg-blue-100 text-blue-800 ring-2 ring-blue-300' : 'border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                          title={`${wd === null ? 'Woche ohne Termin' : `Termin am ${WEEKDAY_SHORT[wd]}`} · in ${count} KW offen`}
                      >
                          <GripVertical className="w-3 h-3 text-slate-300 shrink-0" />
                          {wd === null ? 'ohne Termin' : WEEKDAY_SHORT[wd]}
                          <span className="text-[10px] bg-orange-100 text-orange-700 px-1 rounded-full">{count}</span>
                      </span>
                  );
              })}
          </div>
      );
  };

  /** Projektablage — gemeinsam für beide Matrixansichten (oben horizontal, rechts vertikal). */
  const renderProjectShelf = (inSidePanel = false) => (
      <div className="min-w-0">
          <div className={`flex items-center gap-2 mb-1.5 ${inSidePanel ? 'flex-wrap' : ''}`}>
              <AlertCircle className="w-3 h-3 text-orange-500 shrink-0" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-orange-600">Ohne Vertretung im Zeitraum</span>
              <span className="text-[11px] text-slate-400">({matrixSearch.trim() ? `${shelfProjects.length} von ${openProjectCount}` : shelfProjects.length})</span>
              <span className={`text-[11px] text-slate-400 italic ${inSidePanel ? 'basis-full' : 'ml-auto'}`}>Chip ziehen oder antippen, dann Zelle wählen · mehrere Termine pro Woche: ein Chip je Wochentag</span>
          </div>
          {shelfProjects.length === 0 && openProjectCount > 0 ? (
              <div className="p-4 text-center text-slate-500 text-xs font-bold bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center gap-2">
                  <Search className="w-4 h-4" /> Kein offenes Projekt passt zu „{matrixSearch.trim()}".
              </div>
          ) : shelfProjects.length === 0 ? (
              <div className="p-4 text-center text-emerald-600 text-xs font-bold bg-emerald-50 rounded-xl border border-emerald-100 flex items-center justify-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Alle Projekte sind im Zeitraum vertreten.
              </div>
          ) : (
              <div className={`gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200 min-h-[52px] overflow-y-auto ${inSidePanel ? 'flex flex-col max-h-[65vh]' : 'flex flex-wrap max-h-52'}`}>
                  {shelfProjects.map(p => projectChip(p))}
              </div>
          )}
      </div>
  );

  /** Eine Matrixzeile. `readOnly`: Vertreter mit gespeicherter Vertretung, aber ohne Zeile in der Kollegenliste — nur anzeigen. */
  const renderMatrixRow = (u: UserProfile, readOnly: boolean) => (
                      <tr key={u.id}>
                          <td className="px-3 py-2 font-bold text-slate-700 sticky left-0 z-10 bg-white whitespace-nowrap">
                              <div className="flex items-center gap-1.5"><User className={`w-3.5 h-3.5 shrink-0 ${readOnly ? 'text-slate-300' : 'text-emerald-500'}`} />{u.first_name} {u.last_name}</div>
                              {readOnly && (gesperrteMails.has((u.email || '').trim().toLowerCase())
                                  ? <div className="text-[10px] font-normal text-red-600" title="Gesperrtes Mitglied. Vorhandene Einträge bleiben sichtbar, neue Zuweisungen sind hier nicht möglich.">gesperrt</div>
                                  : <div className="text-[10px] font-normal text-slate-400" title="Gespeicherte Vertretung, der Kollege steht aber nicht in der Vertreterliste (z. B. Profil ohne Namen oder andere E-Mail). Neue Zuweisungen sind hier nicht möglich.">nicht in der Kollegenliste</div>)}
                          </td>
                          {weeks.map(w => {
                              const saved = savedCellEntries(u, w).filter(({ p }) => matchesMatrixSearch(p));
                              const cellPending = pending.filter(a => a.user.id === u.id && weekKey(a.slot.week) === weekKey(w) && matchesMatrixSearch(myProjects.find(p => p.id === a.projectId)));
                              return (
                                  <td
                                      key={weekKey(w)}
                                      onDragOver={(e) => { if (readOnly) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                                      onDrop={(e) => { if (readOnly) return; e.preventDefault(); const chip = parseChip(e.dataTransfer.getData('text/plain')); if (chip) assignToCell(chip, u, w); }}
                                      onClick={() => { if (grabbed && !readOnly) assignToCell(grabbed, u, w); }}
                                      className={`px-1.5 py-1.5 align-top border-l border-slate-100 transition-colors ${grabbed && !readOnly ? 'cursor-copy hover:bg-blue-50' : ''}`}
                                  >
                                      <div className="flex flex-col gap-1 min-h-[34px]">
                                          {saved.map(({ p, entry, label }) => (
                                              <div key={`s_${p.id}_${entry.startDate}_${entry.endDate}`} className="flex items-center gap-1 px-1.5 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-700" title={`${p.name} · gespeichert (${formatDate(entry.startDate)}–${formatDate(entry.endDate)})`}>
                                                  <CheckCircle2 className="w-3 h-3 shrink-0" />
                                                  {(p.data?.metadata as ReportMetadata)?.projectNumber && <span className="font-mono text-[10px] opacity-70 shrink-0">{(p.data?.metadata as ReportMetadata).projectNumber}</span>}
                                                  <span className="truncate font-bold">{p.name || 'Unbenannt'}</span>
                                                  {label && <span className="ml-auto text-[10px] font-bold bg-emerald-100 px-1 rounded shrink-0">{label}</span>}
                                              </div>
                                          ))}
                                          {cellPending.map(a => {
                                              const proj = myProjects.find(p => p.id === a.projectId);
                                              const meta = proj?.data?.metadata as ReportMetadata | undefined;
                                              return (
                                                  <div key={`p_${a.projectId}_${slotKey(a.slot)}`} className="flex items-center gap-1 px-1.5 py-1 rounded bg-blue-50 border border-blue-300 border-dashed text-blue-700" title={`${proj?.name || ''} · ${formatDate(a.slot.startDate)}–${formatDate(a.slot.endDate)} · noch nicht gespeichert`}>
                                                      {meta?.projectNumber && <span className="font-mono text-[10px] opacity-70 shrink-0">{meta.projectNumber}</span>}
                                                      <span className="truncate font-bold">{proj?.name || 'Unbenannt'}</span>
                                                      {a.slot.weekday !== null && <span className="text-[10px] font-bold bg-blue-100 px-1 rounded shrink-0">{WEEKDAY_SHORT[a.slot.weekday]}</span>}
                                                      <button onClick={(e) => { e.stopPropagation(); removePending(a); }} className="ml-auto p-0.5 hover:bg-blue-100 rounded shrink-0"><X className="w-3 h-3" /></button>
                                                  </div>
                                              );
                                          })}
                                      </div>
                                  </td>
                              );
                          })}
                      </tr>
  );

  /** Matrix (Zeilen = Vertreter, Spalten = KWs) — gemeinsam für beide Ansichten. */
  const renderRepresentativeMatrix = () => (
      <div className="overflow-x-auto border border-slate-200 rounded-xl min-w-0">
          <table className="w-full text-left text-xs border-collapse">
              <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2 font-bold text-slate-500 uppercase tracking-wider text-[11px] sticky left-0 z-10 bg-slate-50 min-w-[140px]">Vertreter</th>
                      {weeks.map(w => (
                          <th key={weekKey(w)} className="px-2 py-2 text-center min-w-[160px]">
                              <div className="font-bold text-slate-700">KW {w.kw}</div>
                              <div className="text-[10px] text-slate-400 font-normal">{formatDayMonth(w.weekStart)}–{formatDayMonth(w.weekEnd)}</div>
                          </th>
                      ))}
                  </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                  {vertreterAuswahl.length === 0 && gesperrteMitOffenen.length === 0 && foreignSubstitutes.length === 0 && (
                      <tr><td colSpan={weeks.length + 1} className="px-3 py-6 text-center text-slate-400 italic">Keine Kollegen gefunden.</td></tr>
                  )}
                  {vertreterAuswahl.map(u => renderMatrixRow(u, false))}
                  {gesperrteMitOffenen.map(u => renderMatrixRow(u, true))}
                  {foreignSubstitutes.map(u => renderMatrixRow(u, true))}
              </tbody>
          </table>
      </div>
  );

  /** Hinterlegte Urlaube der geplanten Person als Schnellwahl; gewählten Zeitraum als Urlaub eintragen. */
  const renderVacationStrip = () => {
      const rangeValid = !!data.startDate && !!data.endDate && data.startDate <= data.endDate;
      const alreadyStored = upcomingVacations.some(v => v.startDate === data.startDate && v.endDate === data.endDate);
      if (upcomingVacations.length === 0 && !rangeValid) return null;
      return (
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-1.5">
              <Palmtree className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1">
                  Urlaub{planningForOther ? ` ${planForName}` : ''}
              </span>
              {upcomingVacations.length === 0 && <span className="text-[11px] text-slate-400 italic">keiner hinterlegt</span>}
              {upcomingVacations.map(v => {
                  const active = v.startDate === data.startDate && v.endDate === data.endDate;
                  return (
                      <button
                          key={v.id}
                          type="button"
                          onClick={() => { setData(prev => ({ ...prev, startDate: v.startDate, endDate: v.endDate })); setSaveError(null); }}
                          className={`px-2 py-1 rounded-lg border text-[11px] font-bold transition-colors ${active ? 'border-emerald-500 bg-emerald-100 text-emerald-800' : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300 hover:bg-emerald-50'}`}
                          title="Zeitraum übernehmen"
                      >
                          {formatDayMonth(v.startDate)}–{formatDate(v.endDate)}
                      </button>
                  );
              })}
              {rangeValid && !alreadyStored && (
                  <button
                      type="button"
                      onClick={handleAddVacation}
                      disabled={vacationBusy}
                      className="ml-auto flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 transition-colors"
                      title={`Zeitraum in der Urlaubsplanung von ${planForName} hinterlegen`}
                  >
                      {vacationBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                      Als Urlaub eintragen
                  </button>
              )}
          </div>
      );
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
        <div className={`bg-white rounded-2xl shadow-2xl w-full overflow-hidden flex flex-col h-full max-h-[94vh] transition-all ${activeTab === 'matrix' ? 'max-w-[96vw]' : 'max-w-5xl'}`}>
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 flex justify-between items-center text-white shrink-0">
                <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg"><Palmtree className="w-6 h-6 text-white" /></div>
                    <div>
                        <h3 className="font-bold text-lg">Urlaubsplaner</h3>
                        <p className="text-emerald-100 text-xs">{planningForOther ? `Vertretungen für ${planForName} planen` : 'Vertretungen für Ihre Projekte planen'}</p>
                    </div>
                </div>
                <button onClick={onClose} className="hover:bg-white/10 p-1 rounded transition-colors"><X className="w-5 h-5"/></button>
            </div>

            {/* Für Mitarbeiter planen (Admin oder Recht): Projekte, Vertreterliste und Urlaube wechseln mit */}
            {darfFuerAnderePlanen && (
                <div className="px-5 py-2.5 bg-emerald-50 border-b border-emerald-100 flex flex-wrap items-center gap-2 shrink-0">
                    <Users className="w-4 h-4 text-emerald-600 shrink-0" />
                    <label htmlFor="holiday-plan-for" className="text-xs font-bold text-emerald-800">Planen für</label>
                    <select
                        id="holiday-plan-for"
                        value={planForId}
                        onChange={(e) => changePlanFor(e.target.value)}
                        disabled={loading || pending.length > 0}
                        title={pending.length > 0 ? 'Erst die Zuweisungen speichern oder entfernen' : undefined}
                        className="flex-1 min-w-[180px] max-w-xs px-2 py-1.5 border border-emerald-200 rounded-lg bg-white text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none disabled:opacity-50"
                    >
                        {!plannableEmployees.some(u => u.id === userProfile.id) && (
                            <option value={userProfile.id}>{myName || userProfile.email} (ich)</option>
                        )}
                        {plannableEmployees.map(u => (
                            <option key={u.id} value={u.id}>{fullName(u)}{u.id === userProfile.id ? ' (ich)' : ''}</option>
                        ))}
                    </select>
                    {pending.length > 0 ? (
                        <span className="text-[11px] text-amber-700">Wechsel erst nach Speichern der Zuweisungen</span>
                    ) : planningForOther && (
                        <span className="text-[11px] text-emerald-700">Projekte und Vertretungen von {planForName}</span>
                    )}
                </div>
            )}

            <div className="flex border-b border-slate-200">
                <button onClick={() => { setActiveTab('plan'); setSaveError(null); }} className={`flex-1 py-3 text-sm font-bold text-center transition-colors ${activeTab === 'plan' ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
                    <div className="flex items-center justify-center gap-2"><Calendar className="w-4 h-4" /> Neu planen</div>
                </button>
                <button onClick={() => { setActiveTab('matrix'); setSaveError(null); }} className={`flex-1 py-3 text-sm font-bold text-center transition-colors ${activeTab === 'matrix' ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
                    <div className="flex items-center justify-center gap-2"><LayoutGrid className="w-4 h-4" /> Matrix</div>
                </button>
                <button onClick={() => { setActiveTab('manage'); setSaveError(null); }} className={`flex-1 py-3 text-sm font-bold text-center transition-colors ${activeTab === 'manage' ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
                    <div className="flex items-center justify-center gap-2"><List className="w-4 h-4" /> Geplante ({managedEntries.length})</div>
                </button>
            </div>

            {/* Error Banner */}
            {saveError && (
                <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-bold text-red-700">Fehler</div>
                        <div className="text-xs text-red-600 mt-0.5 whitespace-pre-line">{saveError}</div>
                    </div>
                    <button onClick={() => setSaveError(null)} className="p-1 hover:bg-red-100 rounded"><X className="w-3 h-3 text-red-400" /></button>
                </div>
            )}

            {/* Versand-Status */}
            {mailStatus && (
                <div className="mx-6 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2">
                    {mailBusy ? <Loader2 className="w-4 h-4 text-blue-500 shrink-0 mt-0.5 animate-spin" /> : <Mail className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />}
                    <div className="flex-1 text-xs text-blue-700 whitespace-pre-line">{mailStatus}</div>
                    <button onClick={() => setMailStatus(null)} className="p-1 hover:bg-blue-100 rounded"><X className="w-3 h-3 text-blue-400" /></button>
                </div>
            )}

            {/* Urlaub eingetragen */}
            {vacationStatus && (
                <div className="mx-6 mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                    <div className="flex-1 text-xs text-emerald-700">{vacationStatus}</div>
                    <button onClick={() => setVacationStatus(null)} className="p-1 hover:bg-emerald-100 rounded"><X className="w-3 h-3 text-emerald-400" /></button>
                </div>
            )}

            <div ref={scrollRef} onDragOver={dragAutoScroll} className="flex-1 overflow-y-auto p-6 space-y-5">
                {activeTab === 'plan' ? (
                    <>
                        {/* Step 1: Compact form row */}
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
                                <div>
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Vertreter</label>
                                    <div className="relative">
                                        <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-500" />
                                        <select className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" value={data.substituteEmail} onChange={(e) => { setData({...data, substituteEmail: e.target.value}); setSaveError(null); }}>
                                            <option value="">Bitte wählen...</option>
                                            {users.filter(u => !gesperrteKonten.has(u.id) || u.email === data.substituteEmail).map(u => (<option key={u.id} value={u.email}>{u.first_name} {u.last_name}</option>))}
                                        </select>
                                    </div>
                                    {vertreterAuswahl.length === 0 && <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1"><AlertCircle className="w-2.5 h-2.5"/> Keine Kollegen gefunden</p>}
                                </div>
                                <div className="w-full">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Von</label>
                                    <input type="date" className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" value={data.startDate} onChange={(e) => { setData({...data, startDate: e.target.value}); setSaveError(null); }} />
                                </div>
                                <div className="w-full">
                                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Bis</label>
                                    <input type="date" className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" value={data.endDate} onChange={(e) => { setData({...data, endDate: e.target.value}); setSaveError(null); }} min={data.startDate} />
                                </div>
                            </div>
                            {data.startDate && data.endDate && data.substituteEmail && (
                                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                    <span className="text-xs text-slate-600">
                                        <b className="text-emerald-700">{users.find(u=>u.email===data.substituteEmail)?.first_name}</b> vertritt{planningForOther ? <> <b>{planForName}</b></> : ''} vom <b>{formatDate(data.startDate)}</b> bis <b>{formatDate(data.endDate)}</b>
                                    </span>
                                </div>
                            )}
                            {renderVacationStrip()}
                        </div>

                        {/* Step 2: Project selection */}
                        <div>
                            <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-bold text-slate-700">Projekte</label>
                                    {selectedProjectIds.size > 0 && <span className="text-[11px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{selectedProjectIds.size} gewählt</span>}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={toggleAll} className="text-[11px] text-blue-600 hover:text-blue-800 font-bold px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors">
                                        {filteredProjects.length > 0 && filteredProjects.every(p => selectedProjectIds.has(p.id)) ? 'Keine' : 'Alle'}
                                    </button>
                                </div>
                            </div>

                            {/* Search */}
                            {myProjects.length > 5 && (
                                <div className="mb-2 relative">
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                    <input type="text" placeholder="Suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 outline-none bg-slate-50" />
                                </div>
                            )}

                            {filteredProjects.length === 0 ? (
                                <div className="p-8 text-center text-slate-400 italic bg-slate-50 rounded-xl border border-slate-100">
                                    {myProjects.length === 0 ? 'Keine planbaren Projekte gefunden.' : 'Keine Projekte gefunden.'}
                                </div>
                            ) : (()=>{
                                // Group projects: due (needs coverage) → uncovered → already covered
                                const due: PlanRow[] = [], normal: PlanRow[] = [], covered: PlanRow[] = [];
                                filteredProjects.forEach(p => {
                                    const meta = p.data.metadata as ReportMetadata;
                                    const dueDateStr = getDueDateInPeriod(p, data.startDate, data.endDate);
                                    const entries = getSubstitutions(meta);
                                    // Bei mehreren Terminen je Woche können sich mehrere Kollegen den Zeitraum teilen.
                                    const covering = data.startDate && data.endDate
                                        ? entries.filter(e => e.active && hasOverlap([e], data.startDate, data.endDate))
                                        : [];
                                    const coveringNames = Array.from(new Set(covering.map(e => e.substituteName || e.substituteEmail || ''))).filter(Boolean).join(', ');
                                    if (covering.length > 0) covered.push({ p, meta, dueDateStr, isCovered: true, coveringNames });
                                    else if (dueDateStr) due.push({ p, meta, dueDateStr, isCovered: false, coveringNames: '' });
                                    else normal.push({ p, meta, dueDateStr: null, isCovered: false, coveringNames: '' });
                                });

                                const renderGroup = (title: string, icon: React.ReactNode, items: PlanRow[], color: string, emptyText?: string) => {
                                    if (items.length === 0 && !emptyText) return null;
                                    return (
                                        <div className="mb-3">
                                            <div className={`flex items-center gap-1.5 mb-1.5 px-1`}>
                                                {icon}
                                                <span className={`text-[11px] font-bold uppercase tracking-wider ${color}`}>{title}</span>
                                                <span className="text-[11px] text-slate-400">({items.length})</span>
                                            </div>
                                            {items.length === 0 && emptyText ? (
                                                <div className="text-[11px] text-slate-400 italic px-3 py-2">{emptyText}</div>
                                            ) : (
                                                <div className="space-y-1">
                                                    {items.map(({ p, meta, dueDateStr, isCovered, coveringNames }) => {
                                                        const isSelected = selectedProjectIds.has(p.id);
                                                        return (
                                                            <div key={p.id} onClick={() => !isCovered && toggleProject(p.id)} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all ${
                                                                isCovered ? 'border-slate-100 bg-slate-50/50 opacity-60 cursor-default' :
                                                                isSelected ? 'border-blue-400 bg-blue-50 shadow-sm cursor-pointer' :
                                                                dueDateStr ? 'border-orange-200 bg-orange-50/40 hover:border-orange-300 cursor-pointer' :
                                                                'border-slate-150 bg-white hover:border-slate-300 cursor-pointer'
                                                            }`}>
                                                                {/* Checkbox */}
                                                                {!isCovered && (
                                                                    <div className={`shrink-0 ${isSelected ? 'text-blue-600' : 'text-slate-300'}`}>
                                                                        {isSelected ? <CheckSquare className="w-4 h-4"/> : <Square className="w-4 h-4"/>}
                                                                    </div>
                                                                )}
                                                                {isCovered && <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />}

                                                                {/* Project info */}
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        {meta.projectNumber && <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{meta.projectNumber}</span>}
                                                                        <span className={`text-xs font-bold truncate ${isCovered ? 'text-slate-400' : isSelected ? 'text-slate-800' : 'text-slate-700'}`}>{p.name || 'Unbenannt'}</span>
                                                                    </div>
                                                                </div>

                                                                {/* Right side badges */}
                                                                <div className="shrink-0 flex items-center gap-1.5">
                                                                    {dueDateStr && !isCovered && (
                                                                        <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5">
                                                                            <CalendarClock className="w-2.5 h-2.5"/>{formatDate(dueDateStr)}
                                                                        </span>
                                                                    )}
                                                                    {isCovered && coveringNames && (
                                                                        <span className="text-[10px] bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded border border-emerald-200">
                                                                            {coveringNames}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                };

                                return (<div className="sm:overflow-y-auto sm:max-h-[60vh] pr-1">
                                    {renderGroup('Begehung fällig im Zeitraum', <AlertCircle className="w-3 h-3 text-orange-500"/>, due, 'text-orange-600')}
                                    {renderGroup('Weitere Projekte', <Calendar className="w-3 h-3 text-slate-400"/>, normal, 'text-slate-500')}
                                    {covered.length > 0 && renderGroup('Bereits vertreten', <CheckCircle2 className="w-3 h-3 text-emerald-400"/>, covered, 'text-emerald-600')}
                                </div>);
                            })()}
                        </div>
                    </>
                ) : activeTab === 'matrix' ? (
                    <div className="space-y-4">
                        {/* Zeitraum + Ansicht-Umschalter */}
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                                <div className="grid grid-cols-2 gap-3 max-w-md flex-1">
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Von</label>
                                        <input type="date" className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" value={data.startDate} onChange={(e) => { setData({...data, startDate: e.target.value}); setSaveError(null); }} />
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Bis</label>
                                        <input type="date" className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" value={data.endDate} onChange={(e) => { setData({...data, endDate: e.target.value}); setSaveError(null); }} min={data.startDate} />
                                    </div>
                                </div>
                                <div className="flex-1 min-w-[200px] max-w-md">
                                    <label htmlFor="matrix-project-search" className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Projekt suchen</label>
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                        <input id="matrix-project-search" type="text" value={matrixSearch} onChange={(e) => setMatrixSearch(e.target.value)} placeholder="Name, Nummer oder Adresse" className="w-full pl-8 pr-8 py-2 border border-slate-200 rounded-lg bg-slate-50 text-sm focus:ring-2 focus:ring-emerald-500 outline-none" />
                                        {matrixSearch && (
                                            <button type="button" onClick={() => setMatrixSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded" aria-label="Suche leeren"><X className="w-3.5 h-3.5 text-slate-400" /></button>
                                        )}
                                    </div>
                                </div>
                                <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 self-start sm:self-auto" role="group" aria-label="Matrixansicht">
                                    <button type="button" onClick={() => setMatrixViewMode('projects-above')} aria-pressed={matrixViewMode === 'projects-above'} className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-colors ${matrixViewMode === 'projects-above' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                                        Projekte oben
                                    </button>
                                    <button type="button" onClick={() => setMatrixViewMode('projects-right')} aria-pressed={matrixViewMode === 'projects-right'} className={`px-3 py-1.5 rounded-md text-[11px] font-bold transition-colors ${matrixViewMode === 'projects-right' ? 'bg-white text-emerald-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                                        Projekte rechts
                                    </button>
                                </div>
                            </div>
                            {renderVacationStrip()}
                        </div>

                        {weeks.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 italic bg-slate-50 rounded-xl border border-slate-100 flex flex-col items-center gap-3">
                                Bitte oben einen Zeitraum wählen.
                                {managedEntries.length > 0 && (
                                    <button type="button" onClick={showAllPlanned} className="not-italic px-3 py-1.5 rounded-lg text-xs font-bold text-emerald-700 border border-emerald-200 bg-white hover:bg-emerald-50 transition-colors">
                                        Zeitraum aller {managedEntries.length} geplanten Vertretungen zeigen
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                {matrixCoverage && (matrixCoverage.outside > 0 || matrixCoverage.bySearch > 0) && (
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                        <span>Die Matrix zeigt <b>{matrixCoverage.shown} von {matrixCoverage.total}</b> geplanten Vertretungen.</span>
                                        {matrixCoverage.outside > 0 && <span>{matrixCoverage.outside} liegen außerhalb des Zeitraums.</span>}
                                        {matrixCoverage.bySearch > 0 && <span>{matrixCoverage.bySearch} blendet die Suche aus.</span>}
                                        <span className="ml-auto flex flex-wrap gap-2">
                                            {matrixCoverage.bySearch > 0 && (
                                                <button type="button" onClick={() => setMatrixSearch('')} className="px-2 py-1 rounded-lg font-bold border border-amber-300 bg-white hover:bg-amber-100 transition-colors">Suche leeren</button>
                                            )}
                                            {matrixCoverage.outside > 0 && (
                                                <button type="button" onClick={showAllPlanned} disabled={pending.length > 0} title={pending.length > 0 ? 'Erst die Zuweisungen speichern oder entfernen' : undefined} className="px-2 py-1 rounded-lg font-bold border border-amber-300 bg-white hover:bg-amber-100 disabled:opacity-50 transition-colors">Zeitraum auf alle erweitern</button>
                                            )}
                                        </span>
                                    </div>
                                )}
                                {matrixViewMode === 'projects-above' ? (
                                    <>
                                        {renderProjectShelf()}
                                        {renderRepresentativeMatrix()}
                                    </>
                                ) : (
                                    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-4 items-start">
                                        {renderRepresentativeMatrix()}
                                        <aside className="min-w-0 xl:sticky xl:top-0">
                                            {renderProjectShelf(true)}
                                        </aside>
                                    </div>
                                )}

                                {pending.length > 0 && (
                                    <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2">
                                        <Clock className="w-3.5 h-3.5" />
                                        {pending.length} Zuweisung{pending.length > 1 ? 'en' : ''} noch nicht gespeichert.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {managedEntries.length > 0 && (
                            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-200 mb-4 shadow-sm">
                                <Filter className="w-4 h-4 text-slate-400" />
                                <select className="flex-1 text-sm outline-none bg-transparent text-slate-700 font-medium" value={manageFilter} onChange={(e) => setManageFilter(e.target.value)}>
                                    <option value="">Alle Vertreter anzeigen</option>
                                    {uniqueSubstitutes.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                {manageFilter && <button onClick={() => setManageFilter('')} className="p-1 hover:bg-slate-100 rounded-full"><X className="w-3 h-3 text-slate-400" /></button>}
                            </div>
                        )}

                        {managedPlans.length > 0 && (
                            <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <Mail className="w-4 h-4 text-emerald-600" />
                                    <span className="text-sm font-bold text-emerald-800">Kalender an Vertreter senden</span>
                                </div>
                                <div className="space-y-2">
                                    {managedPlans.map(plan => (
                                        <div key={plan.substituteEmail} className="flex flex-wrap items-center gap-2 bg-white rounded-lg border border-emerald-100 px-3 py-2">
                                            <div className="flex-1 min-w-[180px]">
                                                <div className="text-sm font-bold text-slate-800">{plan.substituteName}</div>
                                                <div className="text-[11px] text-slate-500">
                                                    {plan.substituteEmail} &middot; {plan.assignments.length} Zuweisung{plan.assignments.length === 1 ? '' : 'en'}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDownloadPlanIcs(plan)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                                                title="Kalenderdatei herunterladen"
                                            >
                                                <Download className="w-3.5 h-3.5" /> ICS
                                            </button>
                                            <button
                                                onClick={() => notifySubstitutePlans(plan.assignments, true)}
                                                disabled={mailBusy}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                                                title="E-Mail mit Projektliste und Kalenderdatei senden"
                                            >
                                                {mailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Senden
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {managedEntries.length === 0 ? (
                            <div className="text-center py-12 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
                                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-100" />
                                <p>Keine aktiven Urlaubsvertretungen geplant.</p>
                            </div>
                        ) : filteredManagedEntries.length === 0 ? (
                            <div className="text-center py-12 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
                                <Filter className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                                <p>Keine Vertretungen für diesen Filter gefunden.</p>
                            </div>
                        ) : filteredManagedEntries.map(({ p, entry, idx }) => {
                            const meta = p.data.metadata as ReportMetadata;
                            return (
                                <div key={`${p.id}_${idx}`} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="flex flex-col">
                                                {meta.projectNumber && <span className="text-[11px] font-mono text-slate-400">{meta.projectNumber}</span>}
                                                <h4 className="font-bold text-slate-800">{p.name || 'Unbenanntes Projekt'}</h4>
                                            </div>
                                            <div className="flex items-center gap-2 mt-1 text-sm text-slate-600">
                                                <User className="w-4 h-4 text-emerald-600" />
                                                Vertreter: <span className="font-medium">{entry.substituteName}</span>
                                            </div>
                                        </div>
                                        <button onClick={() => handleCancelSubstitution(p, entry)} disabled={loading} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors" title="Vertretung stornieren">{loading ? <Loader2 className="w-5 h-5 animate-spin"/> : <Trash2 className="w-5 h-5" />}</button>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-3 border border-slate-100">
                                        <Clock className="w-4 h-4 text-slate-400" />
                                        <div className="text-xs text-slate-600">
                                            <span className="block text-slate-400 font-bold uppercase text-[11px]">Zeitraum</span>
                                            {formatDate(entry.startDate)} bis {formatDate(entry.endDate)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="p-5 bg-slate-50 border-t flex flex-wrap justify-end items-center gap-3 shrink-0">
                {activeTab !== 'manage' && (
                    <label className="mr-auto flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={notifySubstitutes}
                            onChange={(e) => setNotifySubstitutes(e.target.checked)}
                            className="w-4 h-4 accent-emerald-600"
                        />
                        <Mail className="w-4 h-4 text-slate-400" />
                        Vertreter per E-Mail informieren (mit Kalenderdatei)
                    </label>
                )}
                <Button variant="ghost" onClick={onClose}>Schließen</Button>
                {activeTab === 'plan' && (
                    <Button variant="primary" onClick={handleSave} disabled={loading || selectedProjectIds.size === 0} className="bg-emerald-600 hover:bg-emerald-700" icon={loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}>
                        Vertretung speichern ({selectedProjectIds.size})
                    </Button>
                )}
                {activeTab === 'matrix' && (
                    <Button variant="primary" onClick={handleSaveMatrix} disabled={loading || pending.length === 0} className="bg-emerald-600 hover:bg-emerald-700" icon={loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}>
                        Zuweisungen speichern ({pending.length})
                    </Button>
                )}
            </div>
        </div>
    </div>
  );
};
